import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Route } from "lucide-react";

const EFFECTS = {
  kept_in_play: {
    badge: "Still in play",
    badgeClass: "bg-emerald-100 text-emerald-800",
  },
  unresolved: {
    badge: "Still unresolved",
    badgeClass: "bg-amber-100 text-amber-900",
  },
  needs_review: {
    badge: "Needs review",
    badgeClass: "bg-rose-100 text-rose-800",
  },
};

const DECISIONS = [
  { code: "accept", label: "Worth it" },
  { code: "maybe", label: "Not sure yet" },
  { code: "reject", label: "Too much" },
];

function fmtUsd(value) {
  if (typeof value !== "number") return null;
  return "$" + Math.round(value).toLocaleString() + "/month";
}

function effectCopy(item) {
  const university = item.university_name || "This university";
  const city = item.city_name || "this city";

  if (item.effect === "kept_in_play") {
    return (
      "You chose to keep " +
      university +
      " in play even though " +
      city +
      "'s living costs were above your saved budget."
    );
  }
  if (item.effect === "needs_review") {
    return (
      "You said " +
      city +
      "'s higher living cost was too much, but " +
      university +
      " is still on your shortlist."
    );
  }
  return (
    "You weren't sure whether " +
    city +
    "'s higher living cost was worth it, and " +
    university +
    " is still on your shortlist."
  );
}

function simulationCopy(preview) {
  const university = preview.university_name || "This university";
  const city = preview.city_name || "this city";

  if (preview.simulated_effect === "kept_in_play") {
    return (
      "If this became your decision, " +
      university +
      " would stay in play and you'd be treating " +
      city +
      "'s higher living cost as worth it."
    );
  }
  if (preview.simulated_effect === "needs_review") {
    return (
      "If this became your decision, " +
      university +
      " would remain on your shortlist, but you'd be saying " +
      city +
      "'s higher living cost is too much."
    );
  }
  return (
    "If this became your decision, " +
    university +
    " would remain on your shortlist and the living-cost trade-off would stay unresolved."
  );
}

function effectKey(item, index) {
  return [
    item.university_name || "",
    item.city_name || "",
    item.decided_at || "",
    index,
  ].join("|");
}

export default function RealisationEffects({ realisationId }) {
  const [effects, setEffects] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [simulatingKey, setSimulatingKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [errorKey, setErrorKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [simulation, setSimulation] = useState(null);

  const loadEffects = useCallback(async () => {
    try {
      const { data } = await api.get(
        "/realisations/" + realisationId + "/effects"
      );
      setEffects(data?.effects || []);
    } catch {
      setEffects([]);
    }
  }, [realisationId]);

  useEffect(() => {
    let alive = true;
    setEffects(null);
    setEditingKey(null);
    setSimulatingKey(null);
    setSavingKey(null);
    setErrorKey(null);
    setErrorMessage("");
    setSimulation(null);

    api
      .get("/realisations/" + realisationId + "/effects")
      .then(({ data }) => {
        if (alive) setEffects(data?.effects || []);
      })
      .catch(() => {
        if (alive) setEffects([]);
      });

    return () => {
      alive = false;
    };
  }, [realisationId]);

  const simulate = async (item, key, response) => {
    if (simulatingKey || savingKey) return;
    setSimulatingKey(key);
    setErrorKey(null);
    setErrorMessage("");
    setSimulation(null);
    try {
      const { data } = await api.post(
        "/realisations/" + realisationId + "/effects/simulate",
        {
          response,
          university_name: item.university_name,
          city_name: item.city_name,
          decided_at: item.decided_at,
        }
      );
      setSimulation({ key, data });
    } catch {
      setSimulation(null);
      setErrorKey(key);
      setErrorMessage(
        "We couldn't preview that decision just now. Refresh and try again."
      );
    } finally {
      setSimulatingKey(null);
    }
  };

  const reconsider = async (item, key, response) => {
    if (savingKey || simulatingKey) return;
    setSavingKey(key);
    setErrorKey(null);
    setErrorMessage("");
    try {
      await api.post(
        "/realisations/" + realisationId + "/effects/reconsider",
        {
          response,
          university_name: item.university_name,
          city_name: item.city_name,
          decided_at: item.decided_at,
        }
      );
      await loadEffects();
      setEditingKey(null);
      setSimulation(null);
      window.dispatchEvent(
        new CustomEvent("wua:realisations-tradeoff-changed", {
          detail: { realisationId },
        })
      );
    } catch {
      setErrorKey(key);
      setErrorMessage(
        "We couldn't make that your decision just now. Refresh and try again."
      );
    } finally {
      setSavingKey(null);
    }
  };

  if (!effects?.length) return null;

  return (
    <section
      className="mt-5 rounded-md border border-violet-200 bg-violet-50/40 p-4 sm:p-5"
      data-testid={"realisation-effects-" + realisationId}
    >
      <div className="flex items-center gap-2">
        <Route className="h-4 w-4 text-[#4C1D95]" />
        <div className="text-[10px] font-underground-sc tracking-[0.14em] text-[#4C1D95]">
          What this means for your shortlist
        </div>
      </div>

      <div className="mt-3 grid gap-3">
        {effects.map((item, index) => {
          const config = EFFECTS[item.effect] || EFFECTS.unresolved;
          const monthlyCost = fmtUsd(item.monthly_cost_usd);
          const monthlyBudget = fmtUsd(item.monthly_budget_usd);
          const overshoot = fmtUsd(item.overshoot_usd);
          const key = effectKey(item, index);
          const editing = editingKey === key;
          const simulating = simulatingKey === key;
          const saving = savingKey === key;
          const preview = simulation?.key === key ? simulation.data : null;
          const previewConfig = preview
            ? EFFECTS[preview.simulated_effect] || EFFECTS.unresolved
            : null;
          const canReconsider = Boolean(
            item.university_name && item.city_name && item.decided_at
          );

          return (
            <div
              key={key}
              className="min-w-0 break-words [overflow-wrap:anywhere] rounded-md border border-stone-200 bg-white px-3.5 py-3.5"
              data-testid={
                "realisation-effect-" + realisationId + "-" + index
              }
            >
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold " +
                    config.badgeClass
                  }
                >
                  {config.badge}
                </span>
                {item.city_name && (
                  <span className="text-xs font-semibold text-[#4C1D95]">
                    {item.city_name}
                  </span>
                )}
              </div>

              {(item.university_name || item.city_name) && (
                <div className="mt-2 text-sm font-semibold text-stone-900">
                  {[item.university_name, item.city_name]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}

              <p className="mt-1.5 text-sm text-stone-700">
                {effectCopy(item)}
              </p>

              {monthlyCost && monthlyBudget && (
                <div className="mt-1.5 text-xs text-stone-500">
                  When you made that decision: about {monthlyCost} against a{" "}
                  {monthlyBudget} living budget
                  {overshoot ? " (" + overshoot + " over)." : "."}
                </div>
              )}

              {canReconsider && !editing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingKey(key);
                    setSimulation(null);
                    setErrorKey(null);
                    setErrorMessage("");
                  }}
                  data-testid={
                    "realisation-effect-reconsider-" +
                    realisationId +
                    "-" +
                    index
                  }
                  className="mt-3 text-xs font-semibold text-[#4C1D95] hover:underline"
                >
                  What if I changed this?
                </button>
              )}

              {editing && (
                <div
                  className="mt-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3"
                  data-testid={
                    "realisation-effect-reconsider-panel-" +
                    realisationId +
                    "-" +
                    index
                  }
                >
                  <div className="text-xs font-semibold text-stone-800">
                    What if you changed this?
                  </div>
                  <div className="mt-1 text-[11px] leading-relaxed text-stone-500">
                    Try a different decision to see what it would mean. Nothing
                    is saved while you're exploring.
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {DECISIONS.map((choice) => {
                      const current = choice.code === item.response;
                      const selected =
                        preview?.simulated_response === choice.code;
                      return (
                        <button
                          key={choice.code}
                          type="button"
                          disabled={simulating || saving || current}
                          onClick={() => simulate(item, key, choice.code)}
                          data-testid={
                            "realisation-effect-reconsider-" +
                            choice.code +
                            "-" +
                            realisationId +
                            "-" +
                            index
                          }
                          className={
                            "min-h-10 rounded-md border px-3 py-2 text-xs font-semibold disabled:cursor-default " +
                            (selected
                              ? "border-[#4C1D95] bg-violet-50 text-[#4C1D95]"
                              : "border-stone-300 bg-white text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]") +
                            (current
                              ? " disabled:bg-stone-100 disabled:text-stone-400"
                              : " disabled:opacity-60")
                          }
                        >
                          {choice.label}
                          {current ? " · Current" : ""}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      disabled={simulating || saving}
                      onClick={() => {
                        setEditingKey(null);
                        setSimulation(null);
                        setErrorKey(null);
                        setErrorMessage("");
                      }}
                      className="min-h-10 px-2 py-2 text-xs text-stone-500 hover:underline disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>

                  {simulating && (
                    <div className="mt-2 text-[11px] text-stone-500">
                      Working out what that would mean…
                    </div>
                  )}

                  {preview && previewConfig && (
                    <div
                      className="mt-3 rounded-md border border-violet-200 bg-white px-3 py-3"
                      data-testid={
                        "realisation-effect-simulation-" +
                        realisationId +
                        "-" +
                        index
                      }
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-[#4C1D95]">
                          Preview only
                        </span>
                        <span
                          className={
                            "rounded-full px-2 py-0.5 text-[10px] font-semibold " +
                            previewConfig.badgeClass
                          }
                        >
                          {previewConfig.badge}
                        </span>
                      </div>

                      <div className="mt-2 text-xs font-semibold text-stone-800">
                        If this became your decision
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-stone-600">
                        {simulationCopy(preview)}
                      </p>
                      <div className="mt-2 text-[11px] leading-relaxed text-stone-500">
                        Nothing has changed. Confirming records a new decision;
                        it still won't remove or re-rank anything.
                      </div>

                      {preview.would_change && (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() =>
                            reconsider(
                              item,
                              key,
                              preview.simulated_response
                            )
                          }
                          data-testid={
                            "realisation-effect-confirm-simulation-" +
                            realisationId +
                            "-" +
                            index
                          }
                          className="mt-3 min-h-10 rounded-md bg-[#4C1D95] px-3 py-2 text-xs font-semibold text-white hover:bg-[#3B0764] disabled:opacity-60"
                        >
                          {saving
                            ? "Saving your new decision…"
                            : "Make this my decision"}
                        </button>
                      )}
                    </div>
                  )}

                  {errorKey === key && (
                    <div className="mt-2 text-[11px] text-rose-700">
                      {errorMessage}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-stone-500">
          WUA hasn't removed or re-ranked anything here.
        </span>
        <Link
          to="/shortlist"
          className="font-semibold text-[#4C1D95] hover:underline"
        >
          Review your shortlist →
        </Link>
      </div>
    </section>
  );
}
